/**
 * The draft application, held in the browser.
 *
 * Form A is fourteen sections long and an applicant will not finish it in one
 * sitting on a phone. Every keystroke is written to local storage, so closing
 * the tab, losing signal or reloading the page loses nothing.
 */

export const DRAFT_KEY = "ksidc.applicant.draft.v1";

export interface Draft {
  formA: Record<string, unknown>;
  clearanceTypeIds: string[];
  /** The step the applicant was last on, so they come back to where they were. */
  step: number;
  savedAt: string;
}

const EMPTY: Draft = { formA: {}, clearanceTypeIds: [], step: 0, savedAt: "" };

export function loadDraft(): Draft {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return { ...EMPTY };
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return { ...EMPTY };
    const d = parsed as Partial<Draft>;
    return {
      formA: d.formA && typeof d.formA === "object" ? d.formA : {},
      clearanceTypeIds: Array.isArray(d.clearanceTypeIds) ? d.clearanceTypeIds : [],
      step: typeof d.step === "number" ? d.step : 0,
      savedAt: typeof d.savedAt === "string" ? d.savedAt : "",
    };
  } catch {
    return { ...EMPTY };
  }
}

export function saveDraft(draft: Draft): void {
  try {
    window.localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ ...draft, savedAt: new Date().toISOString() }),
    );
  } catch {
    // A browser refusing storage should not stop somebody filling the form.
  }
}

export function clearDraft(): void {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Nothing to do.
  }
}

/** Carry the answers from the discovery questions into the form. */
export function seedDraft(seed: {
  formA?: Record<string, unknown>;
  clearanceTypeIds?: string[];
}): void {
  const current = loadDraft();
  saveDraft({
    ...current,
    formA: { ...current.formA, ...(seed.formA ?? {}) },
    clearanceTypeIds:
      seed.clearanceTypeIds && seed.clearanceTypeIds.length > 0
        ? seed.clearanceTypeIds
        : current.clearanceTypeIds,
  });
}

export const hasDraft = (): boolean => {
  const d = loadDraft();
  return Object.keys(d.formA).length > 0 || d.clearanceTypeIds.length > 0;
};
